import React, { createContext, useContext, useState, useEffect } from 'react';

// Translation objektumok import
import en from '../translations/en.json';
import hu from '../translations/hu.json';
import es from '../translations/es.json';
import nl from '../translations/nl.json';
import de from '../translations/de.json';
import fr from '../translations/fr.json';
import pl from '../translations/pl.json';

// ============================================
// TRANSLATIONS OBJEKTUM
// ============================================
const translations = {
  en,
  hu,
  es,
  nl,
  de,
  fr,
  pl,
};

// Elérhető nyelvek listája — sorrend: GB, ES, DE, FR, NL, PL, HU
export const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English',    flag: '🇬🇧' },
  { code: 'es', name: 'Español',    flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch',    flag: '🇩🇪' },
  { code: 'fr', name: 'Français',   flag: '🇫🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski',     flag: '🇵🇱' },
  { code: 'hu', name: 'Magyar',     flag: '🇭🇺' },
];

// ============================================
// I18N CONTEXT
// ============================================
const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  // LÉPÉS 1: Nyelv lekérdezése a localStorage-ből
  // Ha nincs mentve: angol az alapértelmezett
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  // LÉPÉS 2: Nyelvváltás + localStorage mentés
  const setLanguage = (lang) => {
    if (AVAILABLE_LANGUAGES.some(l => l.code === lang)) {
      setLanguageState(lang);
      localStorage.setItem('language', lang);
      // HTML lang attribútum frissítése (SEO + accessibility)
      document.documentElement.lang = lang;
    }
  };

  // LÉPÉS 3: Fordítás függvény
  // Használat: t('auth.login.button') → "Login" (angolul) / "Bejelentkezés" (magyarul)
  const t = (key, defaultValue = key) => {
    try {
      // key: "auth.login.button"
      // translations[language]: az aktuális nyelvű translations objektum
      // split('.'): ['auth', 'login', 'button']
      // reduce: lépkedünk a nested objektumban
      const keys = key.split('.');
      const value = keys.reduce((obj, k) => obj?.[k], translations[language]);
      
      return value || defaultValue;
    } catch (error) {
      console.warn(`Translation not found: ${key}`);
      return defaultValue;
    }
  };

  // LÉPÉS 4: Effect - HTML lang attribútum frissítése
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = {
    language,           // Aktuális nyelv: 'en', 'hu', 'es', 'nl'
    setLanguage,        // Nyelvváltás függvény
    t,                  // Fordítás függvény
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// ============================================
// CUSTOM HOOK: useI18n
// ============================================
// Használat: const { t, language, setLanguage } = useI18n();
export const useI18n = () => {
  const context = useContext(I18nContext);
  
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  
  return context;
};